import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Logo } from "@/components/landing/Logo";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Filter, Eye, Download, FileText, Clock, CheckCircle2,
  XCircle, AlertTriangle, MoreHorizontal, X, Building2, Phone, Mail, Globe,
  User, Calendar, Hash, MessageSquare, Sparkles, Loader2, ChevronRight,
  ExternalLink, LayoutDashboard, ListChecks,
} from "lucide-react";

const STATUS_META = {
  pending:             { label: "Pending",            tone: "bg-amber-50 text-amber-700 border-amber-200",    dot: "bg-amber-500",   icon: Clock },
  documents_required:  { label: "Documents required", tone: "bg-orange-50 text-orange-700 border-orange-200", dot: "bg-orange-500",  icon: AlertTriangle },
  under_review:        { label: "Under review",       tone: "bg-blue-50 text-blue-700 border-blue-200",       dot: "bg-blue-500",    icon: Eye },
  approved:            { label: "Approved",           tone: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", icon: CheckCircle2 },
  rejected:            { label: "Rejected",           tone: "bg-rose-50 text-rose-700 border-rose-200",       dot: "bg-rose-500",    icon: XCircle },
  completed:           { label: "Completed",          tone: "bg-slate-900 text-white border-slate-900",       dot: "bg-white",       icon: CheckCircle2 },
};
const STATUS_ORDER = ["pending","documents_required","under_review","approved","rejected","completed"];
const DOC_LABELS = {
  gst_certificate: "GST Certificate", msme_certificate: "MSME Certificate",
  certificate_of_incorporation: "Certificate of Incorporation", moa: "MOA", aoa: "AOA",
  shop_establishment: "Shop & Estab. Cert.", trade_license: "Trade License",
  electricity_bill: "Electricity Bill", landline_bill: "Landline Bill", company_pan: "Company PAN",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};
const fmtBytes = (b) => {
  if (!b) return "0 B"; const u=["B","KB","MB","GB"]; let i=0,n=b;
  while (n>=1024 && i<u.length-1) { n/=1024; i++; } return `${n.toFixed(n<10&&i>0?1:0)} ${u[i]}`;
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${m.tone}`} data-testid={`status-${status}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
};

export default function AdminOnboarding() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [q, setQ] = useState(params.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(params.get("status") || "all");
  const [companyTypeFilter, setCompanyTypeFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/onboarding/stats").then(r=>r.data),
        api.get("/admin/onboarding", { params: {
          q: q || undefined,
          status: statusFilter !== "all" ? statusFilter : undefined,
          company_type: companyTypeFilter !== "all" ? companyTypeFilter : undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          page, page_size: 20,
        } }).then(r=>r.data),
      ]);
      setStats(s); setData(l);
    } catch {
      toast.error("Failed to load applications");
    } finally { setLoading(false); }
  }, [q, statusFilter, companyTypeFilter, dateFrom, dateTo, page]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (statusFilter !== "all") p.set("status", statusFilter);
    setParams(p, { replace: true });
  }, [q, statusFilter, setParams]);

  // open from query
  useEffect(() => {
    const id = params.get("id");
    if (id && !selectedId) setSelectedId(id);
  }, [params, selectedId]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.page_size || 20)));

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-onboarding-page">
      <header className="sticky top-0 z-20 bg-white/70 backdrop-blur-xl border-b border-slate-200/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={()=>nav("/admin")} className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="back-to-dashboard">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <Logo />
            <span className="hidden sm:inline-flex items-center text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
              Onboarding
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={()=>nav("/admin")} className="rounded-full" data-testid="goto-dashboard"><LayoutDashboard className="h-3.5 w-3.5 mr-1.5" /> Dashboard</Button>
            <span className="hidden sm:block text-xs text-slate-500">Signed in as <b className="text-slate-800">{user?.email}</b></span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8 sm:py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>WhatsApp Onboarding</h1>
            <p className="text-slate-600 mt-1 text-sm">Review applications, manage documents and track status.</p>
          </div>
        </div>

        {/* Stat cards */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="onboarding-stats">
          <StatCard label="Total" value={stats?.total ?? "—"} tone="slate" onClick={()=>setStatusFilter("all")} active={statusFilter==="all"} />
          {STATUS_ORDER.map((s) => (
            <StatCard key={s} label={STATUS_META[s].label}
              value={stats?.by_status?.[s] ?? 0}
              dot={STATUS_META[s].dot}
              onClick={()=>{ setStatusFilter(s); setPage(1); }}
              active={statusFilter===s}
            />
          ))}
        </div>

        {/* Filters */}
        <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-3 flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:flex-1 sm:min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e)=>{ setQ(e.target.value); setPage(1); }}
              placeholder="Search ref ID, company, mobile, email…"
              className="pl-9 rounded-full bg-white border-slate-200"
              data-testid="search-input" />
          </div>
          <Select value={statusFilter} onValueChange={(v)=>{ setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-[170px] rounded-full" data-testid="status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={companyTypeFilter} onValueChange={(v)=>{ setCompanyTypeFilter(v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-[170px] rounded-full" data-testid="company-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {["Proprietorship","Partnership","LLP","Private Limited","Public Limited","OPC","Trust","NGO","Other"].map((t)=> (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={(e)=>{ setDateFrom(e.target.value); setPage(1); }} className="w-full sm:w-[150px] rounded-full" data-testid="date-from" />
          <Input type="date" value={dateTo} onChange={(e)=>{ setDateTo(e.target.value); setPage(1); }} className="w-full sm:w-[150px] rounded-full" data-testid="date-to" />
          {(q || statusFilter!=="all" || companyTypeFilter!=="all" || dateFrom || dateTo) && (
            <Button variant="ghost" size="sm" onClick={()=>{ setQ(""); setStatusFilter("all"); setCompanyTypeFilter("all"); setDateFrom(""); setDateTo(""); }} className="rounded-full" data-testid="clear-filters">
              Clear
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="mt-4 rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="applications-table">
              <thead className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3">Ref ID / Company</th>
                  <th className="px-5 py-3">Authorized person</th>
                  <th className="px-5 py-3">Contact</th>
                  <th className="px-5 py-3">Type</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Submitted</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">
                    <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
                  </td></tr>
                )}
                {!loading && data.items.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-16 text-center text-slate-500" data-testid="applications-empty">
                    <ListChecks className="h-7 w-7 mx-auto text-slate-300" />
                    <div className="mt-2 font-semibold text-slate-700">No applications yet</div>
                    <div className="text-xs">Submissions from the public onboarding page appear here.</div>
                  </td></tr>
                )}
                {!loading && data.items.map((app) => (
                  <tr key={app.id} className="hover:bg-slate-50 cursor-pointer" onClick={()=>setSelectedId(app.id)} data-testid={`row-${app.ref_id}`}>
                    <td className="px-5 py-4">
                      <div className="font-mono text-[11px] text-slate-500">{app.ref_id}</div>
                      <div className="font-semibold text-slate-900">{app.company_name}</div>
                    </td>
                    <td className="px-5 py-4 text-slate-700">{app.auth_person_name}</td>
                    <td className="px-5 py-4">
                      <div className="text-slate-700 text-xs">{app.email}</div>
                      <div className="text-slate-500 text-xs">{app.mobile}</div>
                    </td>
                    <td className="px-5 py-4 text-slate-700 text-xs">{app.company_type}</td>
                    <td className="px-5 py-4"><StatusBadge status={app.status} /></td>
                    <td className="px-5 py-4 text-slate-600 text-xs whitespace-nowrap">{fmtDate(app.created_at)}</td>
                    <td className="px-5 py-4 text-right">
                      <button className="h-8 w-8 rounded-full hover:bg-slate-200 inline-flex items-center justify-center text-slate-600" data-testid={`view-${app.ref_id}`}>
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.total > 0 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-600">
              <span>Showing <b>{(data.page-1)*data.page_size+1}</b>–<b>{Math.min(data.page*data.page_size, data.total)}</b> of <b>{data.total}</b></span>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={data.page<=1} onClick={()=>setPage(p=>Math.max(1,p-1))} className="rounded-full h-7" data-testid="page-prev">Prev</Button>
                <span className="px-2 font-semibold">{data.page} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={data.page>=totalPages} onClick={()=>setPage(p=>p+1)} className="rounded-full h-7" data-testid="page-next">Next</Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {selectedId && (
        <ApplicationDrawer id={selectedId} onClose={()=>{ setSelectedId(null); load(); }} />
      )}
    </div>
  );
}

const StatCard = ({ label, value, tone="slate", dot, onClick, active }) => (
  <button onClick={onClick}
    className={`text-left rounded-2xl border p-4 transition-all bg-white ${active ? "border-slate-900 ring-2 ring-slate-900/10" : "border-slate-200 hover:border-slate-300 hover:-translate-y-0.5"}`}
    data-testid={`stat-${label.toLowerCase().replace(/\s+/g,"-")}`}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">
      {dot && <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />}
      {label}
    </div>
    <div className="mt-1 text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
  </button>
);

/* ------------- Application detail drawer ------------- */
function ApplicationDrawer({ id, onClose }) {
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [publicNotes, setPublicNotes] = useState("");
  const [viewerDoc, setViewerDoc] = useState(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/admin/onboarding/${id}`).then((r) => {
      if (cancel) return;
      setApp(r.data);
      setStatus(r.data.status);
      setAdminNotes(r.data.admin_notes || "");
      setPublicNotes(r.data.public_notes || "");
    }).catch(()=>toast.error("Failed to load application"))
      .finally(()=>!cancel && setLoading(false));
    return () => { cancel = true; };
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/onboarding/${id}`, { status, admin_notes: adminNotes, public_notes: publicNotes });
      setApp(r.data);
      toast.success("Application updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const docUrl = (docId, dl=false) => {
    const tok = localStorage.getItem("wappit_admin_token");
    return `${API}/admin/onboarding/${id}/documents/${docId}?download=${dl?1:0}&token=${encodeURIComponent(tok || "")}`;
  };
  const openDoc = async (doc, dl=false) => {
    try {
      const r = await api.get(`/admin/onboarding/${id}/documents/${doc.id}?download=${dl?1:0}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: doc.mime }));
      if (dl) {
        const a = document.createElement("a"); a.href = url; a.download = doc.file_name;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(()=>URL.revokeObjectURL(url), 1500);
      } else {
        setViewerDoc({ ...doc, blobUrl: url });
      }
    } catch { toast.error("Cannot open document"); }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="application-drawer">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full sm:max-w-2xl bg-white shadow-2xl flex flex-col">
        <div className="px-5 sm:px-7 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[11px] font-mono text-slate-500" data-testid="drawer-ref-id">{app?.ref_id || "…"}</div>
            <div className="text-lg font-bold text-slate-900 truncate">{app?.company_name || "Loading…"}</div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="drawer-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5">
          {loading || !app ? (
            <div className="text-center text-slate-500 py-10"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
          ) : (
            <>
              <StatusBadge status={app.status} />

              {/* Status updater */}
              <div className="mt-5 rounded-2xl border border-slate-200 p-4 bg-slate-50">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex-1 min-w-[180px]">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Update status</label>
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger className="mt-1 bg-white rounded-xl" data-testid="status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_ORDER.map((s)=>(<SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={save} disabled={saving} className="rounded-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5"/> : <CheckCircle2 className="h-4 w-4 mr-1.5"/>}
                    Save changes
                  </Button>
                </div>
              </div>

              {/* Summary grid */}
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InfoCard icon={Building2} label="Company">
                  <KV k="Name" v={app.company_name} />
                  <KV k="Type" v={app.company_type} />
                  <KV k="Website" v={app.company_website} link />
                  <KV k="GST" v={app.gst_number || "—"} />
                  <KV k="Address" v={app.company_address || "—"} />
                </InfoCard>
                <InfoCard icon={User} label="Authorized person">
                  <KV k="Name" v={app.auth_person_name} />
                  <KV k="Mobile" v={app.mobile} />
                  <KV k="Email" v={app.email} />
                </InfoCard>
                <InfoCard icon={Phone} label="WhatsApp">
                  <KV k="Plan" v={app.number_option === "existing" ? "Bringing own number" : "New Virtual Number"} />
                  <KV k="Number" v={app.whatsapp_number || (app.number_option === "virtual" ? "Will be assigned" : "—")} />
                  <KV k="Display name" v={app.display_name} />
                </InfoCard>
                <InfoCard icon={Sparkles} label="Business">
                  <KV k="Category" v={app.business_category || "—"} />
                  <KV k="Use case" v={app.use_case?.replace("_"," ")} />
                  <KV k="Meta Business Manager" v={(app.has_meta_business_manager || "").replace("_"," ")} />
                  <KV k="Facebook" v={app.facebook_url || "—"} link />
                  <KV k="Instagram" v={app.instagram_url || "—"} link />
                  {app.business_description && (
                    <div className="mt-2 text-xs text-slate-600 leading-relaxed border-t border-slate-100 pt-2">{app.business_description}</div>
                  )}
                </InfoCard>
              </div>

              {/* Documents */}
              <div className="mt-5">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-slate-700" />
                  <h3 className="text-sm font-bold text-slate-900">Uploaded documents ({app.documents?.length || 0})</h3>
                </div>
                <ul className="rounded-2xl bg-white border border-slate-200 divide-y divide-slate-100" data-testid="documents-list">
                  {(app.documents || []).map((d) => (
                    <li key={d.id} className="px-4 py-3 flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                        <FileText className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-900">{DOC_LABELS[d.type] || d.type}</div>
                        <div className="text-xs text-slate-500 truncate">{d.file_name} · {fmtBytes(d.size)} · uploaded {fmtDate(d.uploaded_at)}</div>
                      </div>
                      <button onClick={()=>openDoc(d, false)} className="h-8 px-3 rounded-full bg-slate-100 hover:bg-slate-200 text-xs font-semibold inline-flex items-center gap-1.5" data-testid={`preview-${d.id}`}>
                        <Eye className="h-3.5 w-3.5" /> Preview
                      </button>
                      <button onClick={()=>openDoc(d, true)} className="h-8 w-8 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-600" data-testid={`download-${d.id}`}>
                        <Download className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Notes */}
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Internal notes (admin only)</label>
                  <Textarea value={adminNotes} onChange={(e)=>setAdminNotes(e.target.value)} placeholder="Private notes for your team" className="mt-1 rounded-xl min-h-[100px]" data-testid="admin-notes" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Public notes (visible to customer)</label>
                  <Textarea value={publicNotes} onChange={(e)=>setPublicNotes(e.target.value)} placeholder="Will be sent to the customer in update emails" className="mt-1 rounded-xl min-h-[100px]" data-testid="public-notes" />
                </div>
              </div>

              {/* Timeline */}
              <div className="mt-6">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-4 w-4 text-slate-700" />
                  <h3 className="text-sm font-bold text-slate-900">Activity timeline</h3>
                </div>
                <ol className="relative pl-5 border-l-2 border-slate-100 space-y-3" data-testid="timeline">
                  {(app.timeline || []).slice().reverse().map((t, i) => (
                    <li key={i} className="relative">
                      <span className="absolute -left-[26px] top-1 h-3 w-3 rounded-full bg-emerald-500 ring-4 ring-white" />
                      <div className="text-sm font-semibold text-slate-900">{t.message}</div>
                      <div className="text-[11px] text-slate-500">{t.actor} · {fmtDate(t.ts)}</div>
                    </li>
                  ))}
                </ol>
              </div>
            </>
          )}
        </div>
      </div>

      {viewerDoc && (
        <DocumentViewer doc={viewerDoc} onClose={()=>{ URL.revokeObjectURL(viewerDoc.blobUrl); setViewerDoc(null); }} />
      )}
    </div>
  );
}

const InfoCard = ({ icon: Icon, label, children }) => (
  <div className="rounded-2xl border border-slate-200 p-4 bg-white">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="h-3.5 w-3.5 text-slate-500" />
      <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{label}</span>
    </div>
    <div className="space-y-1">{children}</div>
  </div>
);
const KV = ({ k, v, link }) => (
  <div className="flex items-start justify-between gap-3 text-sm">
    <dt className="text-slate-500 text-xs">{k}</dt>
    <dd className="text-slate-900 text-right font-semibold truncate max-w-[60%]">
      {link && v && v !== "—" ? <a href={v.startsWith("http") ? v : `https://${v}`} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-1">{v}<ExternalLink className="h-3 w-3" /></a> : v}
    </dd>
  </div>
);

function DocumentViewer({ doc, onClose }) {
  const isImage = (doc.mime || "").startsWith("image/");
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/80 flex items-center justify-center p-4" onClick={onClose} data-testid="document-viewer">
      <div className="relative max-w-4xl max-h-[90vh] w-full bg-white rounded-2xl overflow-hidden flex flex-col" onClick={(e)=>e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs text-slate-500">{DOC_LABELS[doc.type] || doc.type}</div>
            <div className="font-semibold text-slate-900 truncate">{doc.file_name}</div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="viewer-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 bg-slate-100 overflow-auto">
          {isImage ? (
            <img src={doc.blobUrl} alt={doc.file_name} className="max-w-full mx-auto block" />
          ) : (
            <iframe src={doc.blobUrl} title={doc.file_name} className="w-full h-[80vh] bg-white" />
          )}
        </div>
      </div>
    </div>
  );
}
